import React, { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { AppState } from '../../../store';
import { connect } from 'react-redux';
import {
  AvailabilityCalendar,
  Button as KitButton,
  createDateRange,
  EButtonType,
  EInputType,
  getDatesDiffDays,
  getNumberOfDaysInConcern,
  Input,
  KitIcons,
  parseToApiDate,
  SpinnerV2,
  Tooltip,
} from '@repo/apg-ui';
import { searchActions } from '../../../store/search';
import { productsActions } from '../../../store/products';
import { Loading, UiActions } from '../../../store/ui';
import 'react-date-range/dist/theme/default.css';
import 'react-date-range/dist/styles.css';
import { Modal } from 'react-bootstrap';
import './EditModal.scss';
import classNames from 'classnames';
import { SearchParameters } from '../../../store/search/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { createAvailabilityDates, createCalenderRangesFromDateRange } from '../../../utils/calender';
import { createSubProductUrn } from '../../../utils/products';

interface Props {
  uiActions: typeof UiActions;
  searchActions: typeof searchActions;
  actionsProduct: typeof productsActions;
  show: boolean;
  isAdditionalPeriod: boolean;
  productUrn: string;
  setHide: any;
  possibleBookingDates: any;
  searchParameters: SearchParameters;
  searchLocations: any;
  setProductFilterData: any;
  selectedProducts: any;
  deselectAll: any;
  productFiltersByUrn: any;
  loading: Loading;
}

const EditModal = ({
  show,
  setHide,
  possibleBookingDates,
  setProductFilterData,
  selectedProducts,
  searchParameters,
  uiActions,
  deselectAll = () => {},
  searchActions,
  actionsProduct,
  productFiltersByUrn,
  isAdditionalPeriod = false,
  productUrn,
  loading,
}: Props) => {
  const intl = useIntl();
  const [availability, setAvailability] = useState<any>([]);
  const [timeslots, setTimeslots] = useState<any>([
    { name: 'Day', timespan: '05:00 - 01:00', timeslot: 1 },
    { name: 'Primetime morning', timespan: '05:00 - 09:00', timeslot: 11 },
    { name: 'Shopping', timespan: '09:00 - 16:00', timeslot: 12 },
    { name: 'Primetime evening', timespan: '16:00 - 20:00', timeslot: 13 },
    { name: 'Evening', timespan: '20:00 - 01:00', timeslot: 14 },
  ]);
  const [selectedTimeslot, setSelectedTimeslot] = useState(searchParameters.timeslot);
  const [focusedRange, setFocusedRange] = React.useState<[number, number] | undefined>([0, 0]);
  const { isBudgetSelected, isFrequencySelected } = searchParameters;
  const [dateRanges, setDateRanges] = React.useState(() => {
    if (searchParameters?.dateRanges?.length) {
      return [
        {
          ...searchParameters.dateRanges[0],
          startDate: new Date(searchParameters.dateRanges[0].startDate),
          endDate: new Date(searchParameters.dateRanges[0].endDate),
        },
      ];
    }
    return [];
  });
  const [startDate, setStartDate] = React.useState(searchParameters.startDate);
  const [duration, setDuration] = React.useState<number>(searchParameters.duration);
  const [frequency, setFrequency] = React.useState<number>(searchParameters.frequency);
  const [spotLength, setSpotLength] = React.useState<number>(searchParameters.spotLength);
  const [budget, setBudget] = React.useState<number>(searchParameters.budget);

  const [disableTimeslots, setDisableTimeslots] = React.useState<any>([]);
  const [priceLoading, setPriceLoading]: any = React.useState<boolean>(false);
  const [shareOfTimeLoading, setShareOfTimeLoading]: any = React.useState<boolean>(false);

  useEffect(() => {
    if (possibleBookingDates?.startdigital?.date && possibleBookingDates?.end?.date) {
      setAvailability(
        createAvailabilityDates(possibleBookingDates?.startdigital?.date, possibleBookingDates?.end?.date),
      );
    }
  }, [possibleBookingDates]);

  useEffect(() => {
    setBudget(searchParameters.budget);
    setFrequency(searchParameters.frequency);
    setSpotLength(searchParameters.spotLength);
    if (selectedProducts.length > 0) {
      setStartDate(searchParameters.startDate);
      setSelectedTimeslot(searchParameters.timeslot);
    } else {
      setFrequency(selectedProducts.frequency);
      setDuration(selectedProducts?.pricedays);
      setTimeslots(selectedProducts?.timeslotsinformation);
      setStartDate(selectedProducts.startdate);
      setSpotLength(selectedProducts.spotlength);

      if (selectedProducts?.startdate && selectedProducts?.pricedays) {
        const productRange = createDateRange(new Date(selectedProducts?.startdate), selectedProducts?.pricedays, {
          toEndOfYear: false,
          fullWeeks: false,
        });

        setDateRanges([
          {
            ...productRange,
            startDate: productRange.startDate,
            endDate: productRange.endDate,
          },
        ]);
      }

      const productFilters = isAdditionalPeriod
        ? productFiltersByUrn[productUrn]
        : productFiltersByUrn[selectedProducts.producturn];
      if (productFilters === undefined) {
        return;
      }

      const productFiltersKeys = Object.keys(productFilters);

      if (productFiltersKeys.includes('budget')) {
        setBudget(productFilters.budget);
      }

      if (productFiltersKeys.includes('timeslots') && productFilters?.timeslots?.length) {
        setSelectedTimeslot(productFilters.timeslots[0]);

        if (productFiltersByUrn[selectedProducts?.producturn]?.timeslots?.length > 1) {
          let timeslots = productFiltersByUrn[selectedProducts?.producturn]?.timeslots;
          timeslots = timeslots.filter((timeslot) => timeslot !== productFilters.timeslots[0]);
          setDisableTimeslots(timeslots);
        }
      }

      if (productFiltersKeys.includes('frequency')) {
        setFrequency(productFilters.frequency);
      }

      if (productFiltersKeys.includes('spotlength')) {
        setSpotLength(productFilters.spotlength);
      }

      if (productFiltersKeys.includes('startdate')) {
        setStartDate(productFilters.startdate);
      }

      if (
        productFiltersKeys.includes('dateRanges') &&
        productFiltersKeys.includes('duration') &&
        productFilters.dateRanges.length > 0
      ) {
        setDateRanges(productFilters.dateRanges[0]?.range);
        setDuration(productFilters.duration);
      }
    }
    setPriceLoading(false);
    setShareOfTimeLoading(false);
  }, []);

  const getNewProductFilterData = (newParameters) => {
    let producturn = isAdditionalPeriod ? productUrn : selectedProducts.producturn;

    const productFilterData = productFiltersByUrn[producturn];
    let newProductFilterData: any = {};
    let newTimeslots: any = [];

    if (productFilterData) {
      if (isAdditionalPeriod) {
        const subproducturn = createSubProductUrn(selectedProducts.producturn, selectedTimeslot);
        newProductFilterData = {
          ...productFilterData,
          ...newParameters,
          subproducturn: subproducturn,
        };

        const mainProductFilterData = productFiltersByUrn[selectedProducts?.producturn];
        if (mainProductFilterData && mainProductFilterData?.timeslots?.length) {
          // replace selected timeslot with productFilterData timeslots[0]
          newTimeslots = mainProductFilterData.timeslots.map((timeslot) => {
            if (productFilterData.timeslots[0] === timeslot) {
              return selectedTimeslot;
            }
            return timeslot;
          });
        }
        producturn = subproducturn;
      } else {
        // replace selected timeslot with productFilterData timeslots[0]
        if (productFilterData && productFilterData?.timeslots?.length) {
          newTimeslots = productFilterData.timeslots.map((timeslot) => {
            if (productFilterData.timeslots[0] === timeslot) {
              return selectedTimeslot;
            }
            return timeslot;
          });
        }

        newProductFilterData = {
          ...productFilterData,
          ...newParameters,
          timeslots: newTimeslots,
        };
        delete newProductFilterData?.producturn;
      }
    } else {
      newProductFilterData = {
        ...newParameters,
      };
    }

    return { producturn, newProductFilterData, newTimeslots };
  };

  const handleSaveChanges = () => {
    uiActions.setLoading('productEditModal', true);
    const newParameters: any = {};
    if (selectedTimeslot) {
      newParameters.timeslots = [selectedTimeslot];
    }
    if (budget && isBudgetSelected) {
      newParameters.budget = budget;
    }
    if (spotLength) {
      newParameters.spotlength = spotLength;
    }
    if (startDate) {
      newParameters.startdate = parseToApiDate(startDate);
    }
    if (duration) {
      newParameters.duration = duration;
    }
    if (frequency && isFrequencySelected) {
      newParameters.frequency = frequency;
    }

    const productFilters = isAdditionalPeriod
      ? productFiltersByUrn[productUrn]
      : productFiltersByUrn[selectedProducts.producturn];
    const filters = {
      ...searchParameters,
      ...productFilters,
      timeslots: productFilters?.timeslots || [searchParameters.timeslot],
      spotLength: productFilters?.spotlength || searchParameters.spotLength,
      dateRanges:
        productFilters?.dateRanges?.length > 0 ? productFilters?.dateRanges[0].range : searchParameters.dateRanges,
    };

    if (
      JSON.stringify([selectedTimeslot]) == JSON.stringify(filters?.timeslots) &&
      spotLength == filters?.spotLength &&
      JSON.stringify(dateRanges) == JSON.stringify(filters?.dateRanges) &&
      budget == filters?.budget &&
      frequency == filters?.frequency
    ) {
      if (selectedProducts.length > 0) {
        selectedProducts.forEach((producturn: any) => {
          actionsProduct.batchDeselectProduct(producturn);
        });
      }
      closeModal(true);
    } else {
      const isPriceItemsChanged =
        JSON.stringify([selectedTimeslot]) !== JSON.stringify(filters?.timeslots) ||
        spotLength !== filters?.spotLength ||
        JSON.stringify(dateRanges) !== JSON.stringify(filters?.dateRanges);
      const isShareOfTimeItemsChanged = budget !== filters?.budget || frequency !== filters?.frequency;

      if (selectedProducts.length > 0) {
        uiActions.setIsShowBottomBar(false);
        if (isPriceItemsChanged) {
          getAllPricesForSelectedProduct({ ...newParameters }, isShareOfTimeItemsChanged);
        } else if (isShareOfTimeItemsChanged) {
          getAllCalculateShareOfTimeForSelectedProduct({
            ...newParameters,
          });
        } else {
          closeModal(true);
        }
      } else {
        let { producturn } = getNewProductFilterData(newParameters);
        if (isPriceItemsChanged) {
          getPriceForProduct(producturn, { ...newParameters }, false, true).then(() => {
            if (isShareOfTimeItemsChanged) {
              calculateShareOfTime(producturn, { ...newParameters }, false, true).then(() => {
                closeModal(true);
              });
            } else {
              closeModal(true);
            }
          });
        } else if (isShareOfTimeItemsChanged) {
          calculateShareOfTime(producturn, { ...newParameters }, false, true).then(() => {
            closeModal(true);
          });
        } else {
          closeModal(true);
        }
      }
    }
  };

  const getAllPricesForSelectedProduct = (newParameters, isShareOfTimeItemsChanged) => {
    const pricePromises = selectedProducts.map((producturn: any, index) => {
      return getPriceForProduct(producturn, { ...newParameters }, true, index === selectedProducts.length - 1);
    });

    Promise.all(pricePromises).then(() => {
      if (isShareOfTimeItemsChanged) {
        getAllCalculateShareOfTimeForSelectedProduct({
          ...newParameters,
        });
      } else {
        setTimeout(() => {
          closeModal(true);
        }, 0);
      }
    });
  };

  const getAllCalculateShareOfTimeForSelectedProduct = (newParameters) => {
    const shareOfTimePromises = selectedProducts.map((producturn: any, index) => {
      return calculateShareOfTime(producturn, { ...newParameters }, true, index === selectedProducts.length - 1);
    });
    Promise.all(shareOfTimePromises).then(() => {
      setTimeout(() => {
        closeModal(true);
      }, 0);
    });
  };

  const getPriceForProduct = (producturn, newParameters, isMulti, isLast) => {
    return new Promise((resolve, reject) => {
      setPriceLoading(true);
      const payload = { ...newParameters, producturn: producturn };
      const newDateRanges = createCalenderRangesFromDateRange(dateRanges[0]?.startDate, duration);
      let { newProductFilterData, newTimeslots } = getNewProductFilterData(newParameters);
      return actionsProduct.getPriceForProduct(payload).then((result) => {
        if (result?.data?.producturn && isMulti) {
          setProductFilterData(result?.data?.producturn, {
            ...newParameters,
            dateRanges: newDateRanges,
            producturn: result?.data?.producturn,
            isBudgetChanged: false,
            budget: undefined,
            frequency: undefined,
          });

          actionsProduct.batchDeselectProduct(result?.data?.producturn);
        }

        if (result?.data?.producturn && !isMulti) {
          newProductFilterData.isBudgetChanged = false;
          setProductFilterData(producturn, {
            ...newProductFilterData,
            dateRanges: newDateRanges,
          });

          if (isAdditionalPeriod) {
            setProductFilterData(selectedProducts?.producturn, {
              timeslots: newTimeslots,
            });
          }
        }

        resolve(true);
      });
    });
  };

  const calculateShareOfTime = (producturn, newParameters, isMulti, isLast) => {
    return new Promise((resolve, reject) => {
      setShareOfTimeLoading(true);
      let { newProductFilterData } = getNewProductFilterData(newParameters);
      const payload = { ...newParameters, producturn: producturn };
      return actionsProduct.calculateShareOfTime(payload).then((result) => {
        if (result?.data?.producturn && isMulti) {
          const filterData = {
            ...newParameters,
            producturn: result?.data?.producturn,
            isBudgetChanged: true,
          };
          setProductFilterData(filterData?.producturn, filterData);
          actionsProduct.batchDeselectProduct(result?.data?.producturn);
        }

        if (result?.data?.producturn && !isMulti) {
          setProductFilterData(producturn, {
            ...newProductFilterData,
            isBudgetChanged: true,
          });
        }

        resolve(true);
      });
    });
  };

  const closeModal = (force: boolean = false) => {
    if ((priceLoading === false && shareOfTimeLoading === false) || force) {
      setPriceLoading(false);
      setShareOfTimeLoading(false);
      uiActions.setLoading('productEditModal', false);
      uiActions.setIsBatchSelect(false);
      setHide();
    }
  };

  const setIsBudgetOrFrequencySelected = (value) => {
    if (value === 'budget') {
      searchActions.setSearchParameters({
        isBudgetSelected: true,
        isFrequencySelected: false,
        frequency: 0,
      });
    } else {
      searchActions.setSearchParameters({
        isBudgetSelected: false,
        isFrequencySelected: true,
        budget: 0,
      });
    }
  };

  return (
    <Modal show={show} onHide={setHide} backdrop='static' size='xl' keyboard={false}>
      <div
        className={classNames('productEditModal', {
          'has-modal-spinner': loading.productEditModal,
        })}
      >
        <Modal.Header closeButton className='border-bottom'>
          <Modal.Title>
            <h2>
              <FormattedMessage id='ec.checkout.edit' defaultMessage='Edit' />
            </h2>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading?.productEditModal === true && <SpinnerV2 relative />}
          <section className='modal-date-timeslot'>
            <h4 className='pb-4'>
              <FormattedMessage id='ec.checkout.dateTimeslot' defaultMessage='Datum & Timeslot' />
            </h4>
            {dateRanges.length > 0 && (
              <AvailabilityCalendar
                availabilities={availability}
                dateRange={dateRanges}
                duration={duration}
                isMobile={false}
                isAnalogProduct={false}
                dragSelectionEnabled={false}
                focusedRange={focusedRange}
                onRangeFocusChange={(focus) => {
                  setFocusedRange(focus);
                }}
                labels={{
                  weeks: 'weeks',
                  selectWeeksAndChooseDate: 'selectWeeksAndChooseDate',
                  available: 'available',
                  notAvailable: 'notAvailable',
                  disabled: 'disabled',
                  note: 'note',
                  crossYearErrorText: 'crossYearErrorText',
                  days: 'days',
                  exceptionWeek: 'exceptionWeek',
                }}
                locale={intl.locale}
                possibleBookingDates={possibleBookingDates}
                handleDateChange={(startdate, item) => {
                  const newDuration = getNumberOfDaysInConcern(item.selection.endDate, startdate) + 1;

                  const range = createDateRange(new Date(startdate), newDuration, {
                    toEndOfYear: false,
                    fullWeeks: false,
                  });
                  setDateRanges([range]);
                  setStartDate(startdate);
                  setDuration(newDuration);
                }}
              />
            )}
            <div className='timeslot-tabs d-flex bg-white mt-4'>
              {timeslots &&
                Object.keys(timeslots).map((_timeslotId, idx) => {
                  const isDisabled = disableTimeslots?.includes(timeslots[_timeslotId].timeslot);
                  return (
                    <div
                      className={classNames('slot', {
                        disabled: isDisabled,
                        selected: selectedTimeslot === timeslots[_timeslotId].timeslot,
                      })}
                      key={`slot-${idx}`}
                      onClick={() => {
                        if (!isDisabled) {
                          setSelectedTimeslot(timeslots[_timeslotId].timeslot);
                        }
                      }}
                    >
                      <div className='font-14 font-weight-bold'>{timeslots[_timeslotId].name}</div>
                      <div className='font-12'>{timeslots[_timeslotId].timespan}</div>
                    </div>
                  );
                })}
            </div>
          </section>
          <section className='modal-budget-duration'>
            <h4 className='pb-4'>
              <FormattedMessage id='ec.checkout.budgetDuration' defaultMessage='Budget & Dauer' />
            </h4>
            <div className='ms-3 cursor-pointer me-4'></div>
            <div className='d-flex bg-gray-light p-5 gap-5 align-items-center'>
              <div className='d-flex flex-column w-20 '>
                <div className='d-flex flex-row'>
                  <Input
                    name='budget-frequency'
                    value='budget'
                    type='radio'
                    radioOptions={[
                      {
                        checked: true,
                        value: 'budget',
                        onChange: (_, value) => {
                          setIsBudgetOrFrequencySelected(value);
                        },
                        radioExtraStyles: 'modal-input-radio',
                      },
                    ]}
                    checked={isBudgetSelected}
                  />
                  <div className='modal-input-radio-label'>
                    <FormattedMessage id='ec.checkout.budget' defaultMessage='Budget' />
                    <Tooltip content={<FormattedMessage id='ec.checkout.tooltip' defaultMessage='Tooltip' />}>
                      <KitIcons.InfoIcon className='input-radio-label-tooltip' />
                    </Tooltip>
                  </div>
                </div>
                <div className='d-flex flex-column'>
                  <Input
                    name='budget'
                    label='Budget'
                    inputType={EInputType.Text}
                    type='text'
                    placeholder='0'
                    inputWrapperClass='modal-input-text'
                    onChange={(name, value) => setBudget(parseFloat(value) || 0)}
                    value={budget}
                  >
                    <div className='modal-input-text-suffix'>CHF</div>
                  </Input>
                </div>
              </div>
              <div className='d-flex flex-column w-20'>
                <div className='d-flex flex-row'>
                  <Input
                    name='budget-frequency'
                    value='frequency'
                    type='radio'
                    radioOptions={[
                      {
                        checked: isFrequencySelected,
                        value: 'frequency',
                        onChange: (_, value) => setIsBudgetOrFrequencySelected(value),
                        radioExtraStyles: 'modal-input-radio',
                      },
                    ]}
                  />
                  <div className='modal-input-radio-label'>
                    <FormattedMessage id='ec.checkout.frequency' defaultMessage='Frequenz' />
                    <Tooltip content={<FormattedMessage id='ec.checkout.tooltip' defaultMessage='Tooltip' />}>
                      <KitIcons.InfoIcon className='input-radio-label-tooltip' />
                    </Tooltip>
                  </div>
                </div>
                <div className='d-flex flex-column'>
                  <Input
                    name='frequency'
                    inputType={EInputType.Text}
                    label='Frequency'
                    type='text'
                    placeholder='0'
                    inputWrapperClass='modal-input-text'
                    onChange={(name, value) => setFrequency(value)}
                    value={frequency || '0:00'}
                  >
                    <div className='modal-input-text-suffix'>
                      <FormattedMessage id='ec.checkout.minutes' defaultMessage='Minuten' />
                    </div>
                  </Input>
                </div>
              </div>
              <div className='d-flex flex-column w-25 border-start'>
                <div className='d-flex flex-row'>
                  <div className='modal-label d-flex align-items-center'>
                    <FormattedMessage id='ec.checkout.spotLength' defaultMessage='Spotlänge' />
                    <Tooltip content={<FormattedMessage id='ec.checkout.tooltip' defaultMessage='Tooltip' />}>
                      <KitIcons.InfoIcon className='input-radio-label-tooltip' />
                    </Tooltip>
                  </div>
                </div>
                <div className='d-flex flex-column ms-5'>
                  <Input
                    name='spot-length'
                    inputType={EInputType.Text}
                    label='Spot length'
                    type='text'
                    placeholder='0'
                    inputWrapperClass='modal-input-text'
                    onChange={(name, value) => setSpotLength(parseFloat(value) || 0)}
                    value={spotLength}
                  >
                    <div className='modal-input-text-suffix'>
                      <FormattedMessage id='ec.checkout.seconds' defaultMessage='Sekunden' />
                    </div>
                  </Input>
                </div>
              </div>
            </div>
          </section>
        </Modal.Body>
        <Modal.Footer className='top-border'>
          <KitButton
            buttonType={EButtonType.PillLarge}
            isDisabled={loading?.productEditModal}
            onClick={() => {
              handleSaveChanges();
            }}
          >
            {!(dateRanges && selectedTimeslot) ? (
              <FormattedMessage
                id='ec.checkout.selectDateTimeslotFirst'
                defaultMessage='Wählen Sie ein Datum und ein Zeitfenster'
              />
            ) : (
              <FormattedMessage id='ec.checkout.saveChanges' defaultMessage='Änderungen speichern' />
            )}
          </KitButton>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

function mapStateToProps(state: AppState, ownProps: any) {
  return {
    ...ownProps,
    ...state.products,
    ...state.locations,
    ...state.ui,
    searchParameters: state.locations.searchParameters,
    productFiltersByUrn: state.products.productFiltersByUrn,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actionsProduct: bindActionCreators(productsActions as any, dispatch),
    searchActions: bindActionCreators(searchActions as any, dispatch),
    uiActions: bindActionCreators(UiActions as any, dispatch),
    dispatch: dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(EditModal);
